import axios from "axios";

import { getBaseUrl } from "./base-url";
import { adoptAuthToken } from "./adopt-token";

/**
 * Bind an OAuth redirect to the browser tab that started it.
 *
 * The app keeps a random nonce in sessionStorage and sends SHA-256(nonce) to
 * the start endpoints. The API puts a short-lived single-use ticket in the
 * redirect (`?ticket=`). Redeeming POSTs the ticket with the raw nonce; a
 * JWT in `?token=` is only accepted during rollout when this tab still holds
 * the nonce it minted (old API / old callback). A cold `?token=` link is
 * ignored so a crafted URL cannot sign the victim into the attacker's account.
 */

const NONCE_STORAGE_KEY = "od:oauth-nonce";

/** Loose check for values that may appear in a `?email=` redirect. */
export const isPlausibleEmail = (value: string | null | undefined): boolean => {
  if (!value) return false;
  const trimmed = value.trim();
  if (trimmed.length < 3 || trimmed.length > 254) return false;
  // Reject obvious XSS / control characters before any DOM use.
  if (/[\s<>"'`]/.test(trimmed)) return false;
  for (let i = 0; i < trimmed.length; i += 1) {
    const code = trimmed.charCodeAt(i);
    if (code < 32 || code === 127) return false;
  }
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
};

const bytesToBase64Url = (bytes: Uint8Array): string => {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const bytesToHex = (bytes: ArrayBuffer): string => {
  const view = new Uint8Array(bytes);
  let out = "";
  for (let i = 0; i < view.length; i += 1) {
    out += view[i]!.toString(16).padStart(2, "0");
  }
  return out;
};

const webCrypto = (): Crypto => {
  const c = globalThis.crypto;
  if (!c?.getRandomValues || !c.subtle) {
    throw new Error("Web Crypto is not available in this environment.");
  }
  return c;
};

/** Random nonce for one OAuth start → redeem cycle. Stored in sessionStorage. */
export const createOAuthNonce = (): string => {
  const bytes = new Uint8Array(32);
  webCrypto().getRandomValues(bytes);
  const nonce = bytesToBase64Url(bytes);
  try {
    sessionStorage.setItem(NONCE_STORAGE_KEY, nonce);
  } catch {
    // Private mode / quota: still return the value for this navigation.
  }
  return nonce;
};

export const peekOAuthNonce = (): string | null => {
  try {
    return sessionStorage.getItem(NONCE_STORAGE_KEY);
  } catch {
    return null;
  }
};

/** Read and clear the nonce so a second redeem / replay fails client-side too. */
export const takeOAuthNonce = (): string | null => {
  const nonce = peekOAuthNonce();
  try {
    sessionStorage.removeItem(NONCE_STORAGE_KEY);
  } catch {
    // ignore
  }
  return nonce;
};

export const clearOAuthNonce = (): void => {
  try {
    sessionStorage.removeItem(NONCE_STORAGE_KEY);
  } catch {
    // ignore
  }
};

/** SHA-256 hex of the nonce; this is what start endpoints store in `state`. */
export const hashOAuthNonce = async (nonce: string): Promise<string> => {
  const data = new TextEncoder().encode(nonce);
  const digest = await webCrypto().subtle.digest("SHA-256", data);
  return bytesToHex(digest);
};

/**
 * Mint a nonce and its hash for a start call. Call immediately before POST so
 * the stored value matches what the API binds to the consent `state`.
 */
export const prepareOAuthNonce = async (): Promise<{
  nonce: string;
  nonceHash: string;
}> => {
  const nonce = createOAuthNonce();
  return { nonce, nonceHash: await hashOAuthNonce(nonce) };
};

export type RedeemTicketResult =
  | { ok: true; token: string }
  | { ok: false; message: string };

/** Exchange a single-use ticket + the tab's nonce for an OnlyDash JWT. */
export const redeemOAuthTicket = async (
  ticket: string,
  nonce: string,
): Promise<RedeemTicketResult> => {
  try {
    const response = await axios.post<{ token?: string }>(
      `${getBaseUrl()}/auth/oauth/ticket`,
      { ticket, nonce },
    );
    const token = response.data?.token;
    if (!token || typeof token !== "string") {
      return { ok: false, message: "Sign-in ticket had no token." };
    }
    return { ok: true, token };
  } catch (error) {
    // onlydash-api#71: missing/empty fields → 400 with Nest `message: string[]`;
    // unknown/wrong/replayed/expired ticket → 401 with a single string message
    // ("This sign-in link is no longer valid").
    const raw = (
      error as { response?: { data?: { message?: string | string[] } } }
    )?.response?.data?.message;
    const message = Array.isArray(raw)
      ? raw.filter((part) => typeof part === "string").join("; ")
      : typeof raw === "string"
        ? raw
        : "";
    return {
      ok: false,
      message: message || "Could not complete Google sign-in.",
    };
  }
};

export type OAuthHandoffInput = {
  /** Preferred: single-use ticket from the new API callback. */
  ticket?: string | null;
  /**
   * Legacy JWT-in-URL. Only adopted when this tab still holds the nonce it
   * minted for the start call — never from a cold crafted link.
   * TODO(remove-after-rollout): drop once every callback issues tickets only.
   */
  token?: string | null;
  /** When true, ignore ticket and token so a link cannot switch accounts. */
  alreadySignedIn: boolean;
};

export type OAuthHandoffResult =
  | { status: "adopted"; next: string; token: string }
  | { status: "skipped" }
  | { status: "failed"; message: string };

/**
 * Adopt a redirect handoff only for the tab that started OAuth, and never
 * replace an existing signed-in session.
 *
 * Always clears the stored nonce before returning — including skipped and
 * failed outcomes — so a cancelled / link_required / error callback cannot
 * leave a nonce that a later crafted `?token=` would ride.
 */
export const adoptOAuthHandoff = async (
  input: OAuthHandoffInput,
): Promise<OAuthHandoffResult> => {
  const ticket = input.ticket?.trim() || null;
  const tokenParam = input.token?.trim() || null;

  try {
    if (!ticket && !tokenParam) {
      return { status: "skipped" };
    }

    if (input.alreadySignedIn) {
      return { status: "skipped" };
    }

    if (ticket) {
      const nonce = takeOAuthNonce();
      if (!nonce) {
        return {
          status: "failed",
          message: "This sign-in link belongs to another browser tab.",
        };
      }
      const redeemed = await redeemOAuthTicket(ticket, nonce);
      if (!redeemed.ok) {
        return { status: "failed", message: redeemed.message };
      }
      const next = await adoptAuthToken(redeemed.token);
      return { status: "adopted", next, token: redeemed.token };
    }

    // Legacy `?token=` path. Require a nonce so a cold login-CSRF URL is ignored.
    // TODO(remove-after-rollout): delete once the API no longer puts JWTs in the URL.
    const nonce = peekOAuthNonce();
    if (!nonce) {
      return {
        status: "failed",
        message: "This sign-in link is missing a matching start from this tab.",
      };
    }
    takeOAuthNonce();
    try {
      const next = await adoptAuthToken(tokenParam!);
      return { status: "adopted", next, token: tokenParam! };
    } catch {
      return { status: "failed", message: "Sign-in token was not valid." };
    }
  } finally {
    clearOAuthNonce();
  }
};
